import { useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import ImageBlock from "../components/ImageBlock";
import InquiryForm from "../components/InquiryForm";
import { Reveal } from "../components/Reveal";
import { BASE_KEYWORDS, CONTACT_EMAIL, PAGE_KEYWORDS, USE_CASES, breadcrumbSchema } from "../data/brand";
import { MEDIA } from "../data/media";

const NEXT_STEPS = [
  { n: "01", t: "Send the inquiry", d: "Tell us the crew, the date and the occasion. Two minutes, no account needed." },
  { n: "02", t: "We confirm details", d: "A real person from the team follows up about space, timing and the game schedule." },
  { n: "03", t: "Show up", d: "We handle the rest — drinks cold, food hot, screens on." },
];

export default function Groups() {
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("location") || "";

  return (
    <div className="pt-28 md:pt-36" data-testid="groups-page">
      <Seo
        title="Group Events, Watch Parties & Celebrations"
        description="Bring your crew into the Barverse — game days, watch parties, birthdays, after work, team gatherings and celebrations. Send a group inquiry and our team follows up."
        path="/groups"
        jsonLd={breadcrumbSchema("/groups", "Groups")}
        keywords={`${BASE_KEYWORDS}, ${PAGE_KEYWORDS.groups}`}
      />

      <header className="container-x">
        <Reveal>
          <p className="eyebrow">Groups & events</p>
          <h1 className="mt-5 font-display text-5xl uppercase leading-[0.92] text-bv-ink sm:text-7xl lg:text-8xl">
            Bring your crew into the Barverse.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-bv-sub">
            Tell us the crew size and the occasion — we'll take it from there. No booking engines,
            no banquet halls. A real person follows up.
          </p>
        </Reveal>
      </header>

      <section className="container-x mt-14" aria-label="Group occasions">
        <Reveal>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {USE_CASES.map((u, i) => (
              <li
                key={u}
                className="rounded-xl border border-bv-line bg-bv-panel px-4 py-5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-bv-sub"
              >
                <span className="block font-display text-2xl tracking-normal text-bv-flame">{String(i + 1).padStart(2, "0")}</span>
                <span className="mt-2 block">{u}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      <section className="container-x mt-20 grid gap-12 pb-24 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-display text-3xl uppercase text-bv-ink md:text-4xl">
              Send a group inquiry
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-bv-sub">
              This is an inquiry, not an instant booking — our team confirms space and availability
              with you directly.
            </p>
          </Reveal>
          <Reveal delay={0.08} className="mt-8">
            <InquiryForm defaultSlug={preselect} />
          </Reveal>
        </div>

        <aside className="lg:col-span-5">
          <Reveal delay={0.12}>
            <ImageBlock src={MEDIA.groups.src} alt={MEDIA.groups.alt} className="aspect-[16/10] w-full" />
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-6 rounded-2xl border border-bv-line bg-bv-panel p-8">
              <p className="field-label">What happens next</p>
              <ul className="space-y-5">
                {NEXT_STEPS.map((s) => (
                  <li key={s.n} className="flex gap-4">
                    <span className="font-mono text-xs font-bold text-bv-flame">{s.n}</span>
                    <span>
                      <span className="block font-semibold text-bv-ink">{s.t}</span>
                      <span className="mt-1 block text-sm leading-relaxed text-bv-sub">{s.d}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-bv-line pt-5 text-sm text-bv-sub">
                Prefer email?{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-semibold text-bv-flame transition-colors hover:text-white"
                  data-testid="groups-mailto"
                >
                  {CONTACT_EMAIL}
                </a>
              </p>
            </div>
          </Reveal>
        </aside>
      </section>
    </div>
  );
}
